import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PWAInstallPrompt from '../components/features/PWAInstallPrompt';
import { API_BASE_URL } from '../config/api';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const Contact = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useCustomerAuth();
  const [reason, setReason] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [plannerError, setPlannerError] = useState('');

  useEffect(() => {
    const r = (searchParams.get('reason') || '').trim();
    const s = (searchParams.get('summary') || '').trim();
    if (r) setReason((prev) => prev || r);
    if (s) setAiSummary((prev) => prev || s);
  }, [searchParams]);

  useEffect(() => {
    if (typeof window === 'undefined' || embedded) return;
    if (window.location.hash === '#book-support') {
      requestAnimationFrame(() =>
        document.getElementById('book-support')?.scrollIntoView({ behavior: 'smooth' })
      );
    }
  }, [embedded]);

  const handleAiConfirm = async () => {
    if (!reason.trim()) {
      setPlannerError('Please describe the reason for your support call first.');
      return;
    }
    setPlannerError('');
    setAiLoading(true);
    try {
      const message = [
        'Please rewrite this customer support request into a concise confirmation summary.',
        'Return only plain text in 2-4 short bullet points.',
        'Include likely outcomes and what we should prepare for the call.',
        '',
        `Customer reason: ${reason.trim()}`,
      ].join('\n');
      const res = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          context: {
            currentPath: embedded ? '/customer/profile' : '/contact',
            pageTitle: embedded ? 'Customer service' : 'Contact Us',
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'AI confirmation failed');
      }
      setAiSummary((data.response || '').trim());
      if (!(data.response || '').trim()) {
        setPlannerError('AI returned an empty response. Please try again.');
      }
    } catch (err) {
      setPlannerError(err instanceof Error ? err.message : 'AI confirmation failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleBookSupportCall = () => {
    if (!reason.trim()) {
      setPlannerError('Please describe the reason for your support call first.');
      return;
    }
    setPlannerError('');
    const params = new URLSearchParams();
    params.set('reason', reason.trim());
    if (aiSummary.trim()) params.set('summary', aiSummary.trim());
    if (embedded) {
      params.set('tab', 'support');
      navigate(`/customer/profile?${params.toString()}`, { replace: true });
      requestAnimationFrame(() =>
        document.getElementById('customer-support-booking')?.scrollIntoView({ behavior: 'smooth' })
      );
      return;
    }
    const profileQs = new URLSearchParams();
    profileQs.set('tab', 'support');
    profileQs.set('reason', reason.trim());
    if (aiSummary.trim()) profileQs.set('summary', aiSummary.trim());
    const profilePath = `/customer/profile?${profileQs.toString()}`;
    if (isAuthenticated) {
      navigate(profilePath);
      return;
    }
    navigate(`/customer/login?return=${encodeURIComponent(profilePath)}`);
  };

  if (embedded) {
    return (
      <div style={{ marginTop: 4 }}>
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(236, 72, 153, 0.12))',
            borderRadius: '12px',
            padding: '20px',
            border: '2px solid #10b981',
            marginBottom: '16px',
          }}
        >
          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: 0, marginBottom: '10px', color: '#059669' }}>
            In-app support
          </h3>
          <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6, margin: 0 }}>
            Open a ticket above for order help — we reply here in your account. No need to email; everything stays on-platform.
          </p>
        </div>
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(59, 130, 246, 0.12))',
            borderRadius: '12px',
            padding: '20px',
            border: '2px solid #22d3ee',
          }}
        >
          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: 0, marginBottom: '10px', color: '#0891b2' }}>
            Plan your support call
          </h3>
          <p style={{ color: '#4b5563', marginBottom: '14px', lineHeight: 1.6, fontSize: 14 }}>
            Describe what you need, optionally confirm the agenda with AI, then continue to the booking calendar below.
          </p>
          <label style={{ display: 'block', marginBottom: '8px', color: '#059669', fontWeight: 600, fontSize: 14 }}>
            Reason for the call *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Example: I need help with an order update, shipping timing, and changing delivery details."
            rows={4}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: '8px',
              border: '2px solid rgba(16, 185, 129, 0.5)',
              background: '#fff',
              color: '#111827',
              fontSize: '15px',
              boxSizing: 'border-box',
              marginBottom: '14px',
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: aiSummary ? '12px' : '0' }}>
            <button
              type="button"
              onClick={handleAiConfirm}
              disabled={aiLoading}
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                cursor: aiLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {aiLoading ? 'AI confirming…' : 'AI Help Confirm Call Agenda'}
            </button>
            <button
              type="button"
              onClick={handleBookSupportCall}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Continue to booking
            </button>
          </div>
          {plannerError && (
            <p style={{ color: '#b91c1c', marginTop: '10px', marginBottom: 0, fontSize: 14 }}>{plannerError}</p>
          )}
          {aiSummary && (
            <div style={{ marginTop: '14px', padding: '12px', borderRadius: '8px', background: '#f0fdfa', border: '1px solid rgba(34, 211, 238, 0.5)' }}>
              <p style={{ margin: '0 0 6px', color: '#0e7490', fontWeight: 600, fontSize: 14 }}>AI confirmation</p>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#374151', fontFamily: 'inherit', fontSize: '14px' }}>
                {aiSummary}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      color: 'white',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'relative',
        zIndex: 10,
        padding: window.innerWidth <= 768 ? '20px 16px' : '20px',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          maxWidth: '1280px', 
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            style={{
              textAlign: 'center',
              marginBottom: '12px',
              paddingTop: '20px'
            }}
          >
            <img
              src="/favicon.png"
              alt="Rainbow Sails"
              style={{
                width: '25%',
                maxWidth: '200px',
                height: 'auto',
                margin: '0 auto'
              }}
            />
          </motion.div>

          {/* Header */}
          <header style={{ textAlign: 'center', marginBottom: '40px' }}>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{
                fontSize: 'clamp(3rem, 8vw, 5rem)',
                fontWeight: 'bold',
                marginBottom: '24px',
                color: '#10b981'
              }}
            >
              Contact Us
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{
                fontSize: 'clamp(1.25rem, 3vw, 1.875rem)',
                color: '#22d3ee',
                marginBottom: '16px'
              }}
            >
              We support our community — reach out anytime 🌈
            </motion.p>
          </header>

          {/* Community + account CTA */}
          <section style={{ marginBottom: '48px' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.35), rgba(59, 130, 246, 0.25))',
                backdropFilter: 'blur(4px)',
                borderRadius: '12px',
                padding: '28px',
                border: '2px solid #34d399',
                textAlign: 'center',
              }}
            >
              <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', margin: '0 0 12px', color: '#a7f3d0' }}>
                Full support lives in your account
              </h2>
              <p style={{ fontSize: '17px', color: '#e2e8f0', lineHeight: 1.65, maxWidth: '40rem', margin: '0 auto 20px' }}>
                You can checkout as a guest — no problem. To book a support call, message us, and track orders in one place,
                create a free Rainbow Sails account (use the <strong>same email as your order</strong> so everything links up).
              </p>
              {isAuthenticated ? (
                <Link
                  to="/customer/profile?tab=support"
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    padding: '14px 28px',
                    borderRadius: '8px',
                    fontWeight: 700,
                    textDecoration: 'none',
                  }}
                >
                  Open customer service in My Account
                </Link>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
                  <Link
                    to="/customer/signup"
                    style={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      padding: '14px 24px',
                      borderRadius: '8px',
                      fontWeight: 700,
                      textDecoration: 'none',
                    }}
                  >
                    Sign up for support
                  </Link>
                  <Link
                    to={`/customer/login?return=${encodeURIComponent('/customer/profile?tab=support')}`}
                    style={{
                      display: 'inline-block',
                      background: 'rgba(15, 23, 42, 0.6)',
                      color: 'white',
                      padding: '14px 24px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      textDecoration: 'none',
                      border: '2px solid rgba(52, 211, 153, 0.5)',
                    }}
                  >
                    Sign in
                  </Link>
                </div>
              )}
            </motion.div>
          </section>

          {/* Contact Information Section */}
          <section style={{ marginBottom: '64px' }}>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5))',
                backdropFilter: 'blur(4px)',
                borderRadius: '12px',
                padding: '32px',
                border: '2px solid #10b981'
              }}
            >
              <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#10b981' }}>📧 Contact Information</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Email</h3>
                  <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                    General messages and async help: <strong style={{ color: '#f59e0b' }}>support@rainbow-sails.shop</strong>
                    {' '}(booking and live tools use your account — see above).
                  </p>
                </div>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Response Time</h3>
                  <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                    We typically respond within 24 hours during business days. For urgent matters, 
                    please mention "URGENT" in your subject line.
                  </p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Customer support call planner */}
          <section id="book-support" style={{ marginBottom: '64px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(59, 130, 246, 0.3))',
                backdropFilter: 'blur(4px)',
                borderRadius: '12px',
                padding: '32px',
                border: '2px solid #22d3ee',
              }}
            >
              <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '12px', color: '#22d3ee' }}>
                Book Customer Support Call
              </h2>
              <p style={{ color: '#d1d5db', marginBottom: '18px', lineHeight: 1.6 }}>
                Tell us what you need help with, then use AI to confirm what will be covered. Support calls are capped at 15 minutes.
                {!isAuthenticated && (
                  <span style={{ display: 'block', marginTop: '10px', color: '#fde68a' }}>
                    You&apos;ll sign in (or sign up) to pick a time — same email as your order keeps support smooth.
                  </span>
                )}
              </p>
              <label style={{ display: 'block', marginBottom: '8px', color: '#4ade80', fontWeight: 600 }}>
                Reason for the call *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Example: I need help with an order update, shipping timing, and changing delivery details."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: '2px solid rgba(16, 185, 129, 0.5)',
                  background: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  marginBottom: '14px',
                }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: aiSummary ? '12px' : '0' }}>
                <button
                  type="button"
                  onClick={handleAiConfirm}
                  disabled={aiLoading}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    cursor: aiLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {aiLoading ? 'AI confirming…' : 'AI Help Confirm Call Agenda'}
                </button>
                <button
                  type="button"
                  onClick={handleBookSupportCall}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  {isAuthenticated ? 'Continue to booking' : 'Continue to booking (sign in)'}
                </button>
              </div>
              {plannerError && (
                <p style={{ color: '#fca5a5', marginTop: '10px', marginBottom: 0 }}>{plannerError}</p>
              )}
              {aiSummary && (
                <div style={{ marginTop: '14px', padding: '12px', borderRadius: '8px', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(34, 211, 238, 0.5)' }}>
                  <p style={{ margin: '0 0 6px', color: '#67e8f9', fontWeight: 600 }}>AI confirmation</p>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#d1d5db', fontFamily: 'inherit', fontSize: '14px' }}>
                    {aiSummary}
                  </pre>
                </div>
              )}
            </motion.div>
          </section>

          {/* Call to Action Section */}
          <section style={{
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(139, 92, 246, 0.2))',
            borderRadius: '12px',
            padding: '32px',
            border: '2px solid #a78bfa'
          }}>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{
                fontSize: '2.25rem',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#a78bfa'
              }}
            >
              We support our community 🌈
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              style={{
                fontSize: '20px',
                marginBottom: '24px',
                maxWidth: '48rem',
                margin: '0 auto 24px'
              }}
            >
              Questions about art, orders, or the experiment? Guest checkout is welcome — create an account when you&apos;re ready for dedicated support and order tools.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link
                to="/gallery"
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '20px',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #db2777, #7c3aed)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #ec4899, #8b5cf6)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                🚀 Explore the Gallery
              </Link>
            </motion.div>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '16px' }}>
              AI-crafted art • Premium quality • Worldwide shipping • 24/7 support
            </p>
          </section>
        </div>
      </div>
      <PWAInstallPrompt />
    </div>
  );
};

export default Contact;
